import React, { useEffect } from 'react';
import { Tab, TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/react';
import { Form, Formik } from 'formik';
import { isEmpty } from 'lodash';
import usePage from '@dashboard/hooks/usePage';
import { FormElement, Label } from '@dashboard/components/Form';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { TextInput } from '@dashboard/components/Formik';
import FormError from '@dashboard/components/Formik/components/FormError';
import TabButton from '@dashboard/components/TabButton';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import ConfirmationCheckoutMessageEditor from './ConfirmationCheckoutMessageEditor';
import { usePreview } from '@dashboard/context/PreviewContext';

function getTabIndex({ customizeEmail }) {
  if (customizeEmail) return 1;
  return 0;
}

const ReminderEmailForm = ({ reminderNumber }) => {
  const { page, updatePage } = usePage();
  const [, previewDispatch] = usePreview();
  const setPreview = value => previewDispatch({ type: 'set', value });

  const reminderKey = `reminder${reminderNumber}`;
  const reminderData = page?.checkoutAbandonment?.emailReminders?.[reminderKey] || {};

  const defaultSubject = 'Complete your purchase';
  const defaultButtonText = 'Complete order';

  const initialValues = {
    customizeEmail: reminderData.customizeEmail || false,
    subject: reminderData.subject || defaultSubject,
    body: reminderData.body || '',
    buttonText: reminderData.buttonText || defaultButtonText,
  };

  useEffect(() => {
    setPreview({
      type: 'reminderEmailMessage',
      reminderNumber: reminderNumber,
    });
  }, [reminderNumber]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={async (values, actions) => {
        actions.setStatus(undefined);

        const payload = {
          checkoutAbandonment: {
            ...page?.checkoutAbandonment,
            emailReminders: {
              ...page?.checkoutAbandonment?.emailReminders,
              [reminderKey]: {
                ...values,
              },
            },
          },
        };

        const { data, error } = await updatePage.mutateAsync(payload);

        if (data) {
          actions.setStatus('');
          const updatedReminderData =
            data.checkoutAbandonment?.emailReminders?.[reminderKey] || values;

          actions.resetForm({
            values: {
              customizeEmail: updatedReminderData.customizeEmail || false,
              subject: updatedReminderData.subject || defaultSubject,
              body: updatedReminderData.body || '',
              buttonText: updatedReminderData.buttonText || defaultButtonText,
            },
          });
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ values, status, dirty, setFieldValue, handleSubmit, setValues, isSubmitting }) => {
        const tabIndex = getTabIndex(values);

        return (
          <Form onSubmit={handleSubmit}>
            <TabGroup
              defaultIndex={tabIndex}
              onChange={index => {
                switch (index) {
                  case 0:
                    setValues({
                      ...initialValues,
                      customizeEmail: false,
                    });
                    break;
                  case 1:
                    setValues({
                      ...initialValues,
                      customizeEmail: true,
                    });
                    break;
                }
              }}
            >
              <TabList className="flex mt-1 gap-x-2">
                <Tab className="w-1/2 outline-none">
                  {({ selected }) => <TabButton selected={selected}>Reminder email</TabButton>}
                </Tab>
                <Tab className="w-1/2 outline-none">
                  {({ selected }) => (
                    <TabButton selected={selected}>Custom reminder email</TabButton>
                  )}
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel className="mt-4">
                  <FormElement>{status && status}</FormElement>

                  <FormSubmit>
                    <div className="flex items-baseline whitespace-nowrap gap-x-2">
                      <FormSubmit.Button>Save</FormSubmit.Button>
                      <FormSubmit.State visible={!!dirty && !isSubmitting} />
                    </div>
                  </FormSubmit>
                </TabPanel>

                <TabPanel className="mt-4">
                  <FormElement>
                    <Label htmlFor={`${reminderKey}-subject`}>Subject</Label>
                    <TextInput
                      required
                      id={`${reminderKey}-subject`}
                      name="subject"
                      type="text"
                      value={values.subject}
                      className="text-sm"
                    />
                    <FormError name="subject" />
                  </FormElement>

                  <FormElement>
                    <div className="flex justify-between items-baseline">
                      <Label htmlFor={`${reminderKey}-body`} tags={['supports HTML']}>
                        Email body
                      </Label>
                      <span className="text-xs text-gray-500 font-normal text-right ml-auto">
                        Insert variables by typing{' '}
                        <code className="font-mono bg-gray-100 px-1 py-0.5 text-chocolate-1000">
                          {'{'}
                        </code>
                      </span>
                    </div>

                    <ConfirmationCheckoutMessageEditor
                      required
                      id={`${reminderKey}-body`}
                      name="body"
                      page={page}
                      values={values}
                      setFieldValue={setFieldValue}
                      reminderEmail={true}
                    />
                    <FormError name="body" />
                  </FormElement>

                  <FormElement>
                    <Label htmlFor={`${reminderKey}-buttonText`}>Button text</Label>
                    <TextInput
                      required
                      id={`${reminderKey}-buttonText`}
                      name="buttonText"
                      type="text"
                      value={values.buttonText}
                      className="text-sm"
                    />
                    <FormError name="buttonText" />
                  </FormElement>

                  <FormElement>{status && status}</FormElement>

                  <FormSubmit>
                    <div className="flex items-baseline whitespace-nowrap gap-x-2">
                      <FormSubmit.Button>Save</FormSubmit.Button>
                      <FormSubmit.State visible={!!dirty && !isSubmitting} />
                    </div>
                  </FormSubmit>
                </TabPanel>
              </TabPanels>
            </TabGroup>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ReminderEmailForm;
